/**
 * LOS DATOS DE MEDICARE QUE SON DE LA PERSONA, NO DE LA PÓLIZA.
 *
 * Lissette, 2026-09-19: *«en el cliente, en la parte de editar, quiero tener al
 * lado de la pestaña salud una pestaña fija que diga medicare, y que en ella
 * esté lo siguiente: número de medicare y el espacio para llenarlo, al igual
 * que otro para llenar la fecha de parte A y otro para parte B; y que a su vez
 * se haga espejo en las fichas de medicare esos datos»*.
 *
 * ── POR QUÉ ESTO NO ESTABA, ESTANDO YA EN LA PÓLIZA ────────────────────────
 *
 * El MBI, la fecha de Parte A y la de Parte B **ya existen en la póliza** (en
 * `type_details`, para los tipos Medicare Advantage, Supplement y Part D). Pero
 * esos tres datos **no son de la póliza: son de la persona.** Un cliente con un
 * Medicare Advantage y un Part D tiene UN solo MBI y UNA sola fecha de Parte A,
 * y hoy hay que teclearlos en cada póliza — dos sitios donde escribirlos, dos
 * sitios donde equivocarse, y ninguno que sea la respuesta a *«¿cuál es el MBI
 * de esta señora?»*.
 *
 * Por eso van en la FICHA, y la póliza los toma de ahí.
 *
 * ── DÓNDE SE GUARDAN, Y POR QUÉ AHÍ ───────────────────────────────────────
 *
 * En `customValues`, con estas tres claves reservadas. Tres razones:
 *
 *   1. **Ya va cifrado.** `customValues` se guarda en `Client.customTabsEncrypted`
 *      y cada lectura deja una fila de auditoría. El MBI es un identificador
 *      sanitario: es exactamente el trato que necesita, y aquí sale gratis.
 *   2. **No hace falta migración.** Es un mapa `{clave: texto}` libre.
 *   3. **No puede chocar con una pestaña de la agencia.** Los campos que crea
 *      una agencia llevan id `cf_<aleatorio>` (ver `CustomTabsEditor`), así que
 *      un `medicare_mbi` no se cruza con ninguno, ni hoy ni nunca.
 *
 * La pestaña es FIJA —no una de las que la agencia crea— porque estos tres
 * campos los pidió ella para todas las agencias, y una pestaña que cada una
 * tenga que crearse a mano no es la misma función.
 *
 * ── EL ESPEJO VA EN UN SENTIDO, Y A PROPÓSITO ─────────────────────────────
 *
 * La ficha rellena la póliza; la póliza **no** reescribe la ficha. Si fuera en
 * los dos sentidos, abrir una póliza vieja con el MBI mal escrito le pisaría a
 * la ficha el bueno, y nadie sabría cuál era el correcto. Y el relleno solo
 * ocurre cuando el campo de la póliza está **vacío**: lo que una agente
 * escribió en una póliza concreta no se le toca nunca.
 *
 * Sin dependencias: lo importan el formulario del cliente, el de la póliza y
 * las pruebas.
 */

/** Las tres claves reservadas dentro de `customValues`. */
export const MEDICARE_MBI = 'medicare_mbi';
export const MEDICARE_PARTE_A = 'medicare_parte_a';
export const MEDICARE_PARTE_B = 'medicare_parte_b';

/**
 * Los tres campos de la pestaña, en el orden en que se enseñan.
 *
 * `tipo` es el del `<input>`: el MBI es texto y las dos fechas son `date`.
 * Las etiquetas se piden fuera para que este archivo no sepa de idiomas.
 */
export const CAMPOS_DE_MEDICARE = [
  { clave: MEDICARE_MBI, tipo: 'text', es: 'N° de Medicare (MBI)', en: 'Medicare Number (MBI)', ejemplo: '1EG4-TE5-MK73' },
  { clave: MEDICARE_PARTE_A, tipo: 'date', es: 'Fecha Parte A', en: 'Part A Date', ejemplo: '' },
  { clave: MEDICARE_PARTE_B, tipo: 'date', es: 'Fecha Parte B', en: 'Part B Date', ejemplo: '' },
];

/** Lo que la ficha del cliente tiene apuntado. Siempre las tres claves. */
export function medicareDeLaFicha(customValues) {
  const v = (customValues && typeof customValues === 'object' && !Array.isArray(customValues)) ? customValues : {};
  return {
    medicareNumber: String(v[MEDICARE_MBI] || '').trim(),
    partADate: String(v[MEDICARE_PARTE_A] || '').trim(),
    partBDate: String(v[MEDICARE_PARTE_B] || '').trim(),
  };
}

/** ¿Hay algo apuntado en la ficha? Para no ofrecer un espejo vacío. */
export function laFichaTieneMedicare(customValues) {
  const m = medicareDeLaFicha(customValues);
  return !!(m.medicareNumber || m.partADate || m.partBDate);
}

/**
 * EL ESPEJO: los tres campos de la póliza, rellenados desde la ficha.
 *
 * Devuelve **solo lo que hay que cambiar**, y solo para los campos de la póliza
 * que están VACÍOS. Si no hay nada que rellenar devuelve `{}`, y quien llama no
 * tiene que tocar el formulario — lo que evita un re-render y, sobre todo,
 * evita marcar el formulario como "modificado" sin que nadie escribiera nada.
 *
 * Nunca borra: un valor vacío en la ficha no vacía el de la póliza.
 */
export function espejoHaciaLaPoliza(customValues, poliza) {
  const ficha = medicareDeLaFicha(customValues);
  const p = poliza || {};
  const cambios = {};
  for (const campo of ['medicareNumber', 'partADate', 'partBDate']) {
    const enLaPoliza = String(p[campo] == null ? '' : p[campo]).trim();
    if (enLaPoliza || !ficha[campo]) continue;
    cambios[campo] = ficha[campo];
  }
  return cambios;
}

/**
 * ¿Qué hay que ponerle a la ficha para reflejar estos tres valores?
 *
 * Lo usa el formulario del cliente para escribir en `customValues` sin tener
 * que conocer las claves. Un valor vacío BORRA su clave en vez de guardar una
 * cadena vacía: `normalizeCustomValues` en api.js descarta los vacíos de todas
 * formas, y dejar la clave puesta haría que la ficha pareciera tener el dato.
 */
export function ponerMedicareEnLaFicha(customValues, { medicareNumber, partADate, partBDate } = {}) {
  const out = { ...(customValues && typeof customValues === 'object' && !Array.isArray(customValues) ? customValues : {}) };
  const poner = (clave, valor) => {
    const v = String(valor == null ? '' : valor).trim();
    if (v) out[clave] = v;
    else delete out[clave];
  };
  poner(MEDICARE_MBI, medicareNumber);
  poner(MEDICARE_PARTE_A, partADate);
  poner(MEDICARE_PARTE_B, partBDate);
  return out;
}
